"use client"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import Text from "@/app/(site)/_components/Atoms/Text"
import ArticleCard from "@/app/(site)/_components/Organisms/Cards/ArticleCard"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { t } from "@/translations"
import { type RelatedArticles } from "@/types"
import { usePathname } from "next/navigation"
import React, { useEffect, useRef, useState } from "react"

type Props = {
    articles: RelatedArticles[] | undefined
    displayedArticlesInit?: number | undefined
    articleListinUpdate?: number | undefined
    locale: string
}

const JournalGrid: React.FC<Props> = ({
    articles,
    locale,
    displayedArticlesInit = 7, // Default value set to 7
    articleListinUpdate = 8 // Default update count set to 8
}) => {
    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""

    const [showButton, setshowButton] = useState(false)
    const [articleListLength, setArticleListLength] = useState(
        articles?.length ? articles.length : 0
    )
    const [displayedArticles, setDisplayedArticles] = useState(displayedArticlesInit)
    const gridRef = useRef<HTMLDivElement>(null)
    const firstNewArticleIndexRef = useRef<number | null>(null)

    const handleUpdateArrayIndex = () => {
        firstNewArticleIndexRef.current = displayedArticles
        setDisplayedArticles(prevIndex => {
            const newDisplayCount = prevIndex + articleListinUpdate
            if (newDisplayCount >= articleListLength) {
                return articleListLength
            }
            return newDisplayCount
        })
    }

    // New cards are inserted above the button, so the browser would carry focus
    // past them into the footer. Move it to the first card that just appeared.
    useEffect(() => {
        const firstNewIndex = firstNewArticleIndexRef.current
        if (firstNewIndex === null) return
        firstNewArticleIndexRef.current = null

        gridRef.current
            ?.querySelector<HTMLElement>(`[data-article-index="${firstNewIndex}"] a`)
            ?.focus()
    }, [displayedArticles])

    useEffect(() => {
        setArticleListLength(articles?.length ? articles.length : 0)
        if (displayedArticles >= articleListLength) {
            setshowButton(false)
        } else {
            setshowButton(true)
        }
    }, [displayedArticles, articleListLength, articles])

    return (
        <div
            ref={gridRef}
            className="grid w-full grid-cols-2 gap-16 md:gap-24 lg:grid-cols-3 xl:grid-cols-4"
        >
            {articles &&
                articles
                    .slice(0, displayedArticles)
                    .map((article, index) => (
                        <ArticleCard
                            isAboveTheFold={index < 3}
                            key={index}
                            index={index}
                            article={article}
                            locale={locale}
                        />
                    ))}
            {showButton && (
                <button
                    type="button"
                    onClick={handleUpdateArrayIndex}
                    className={clsx(
                        "flex aspect-square items-center justify-center rounded-full border border-black bg-white",
                        FOCUS_VISIBLE_CLASS
                    )}
                >
                    <Text asChild size="medium" weight="regular">
                        <span className="flex items-center justify-center gap-8 text-main-black">
                            <span className="!text-12 md:!text-16">
                                {t(langCode, "category_page.load_more")}
                            </span>
                            <span aria-hidden="true">
                                <IconArrowRight className="color-main-black h-20 w-20" />
                            </span>
                        </span>
                    </Text>
                </button>
            )}
        </div>
    )
}

export default JournalGrid
